/**
 * Widget V2 - FilterDropdown Component
 *
 * Dropdown select for filter options.
 */

import React from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

/**
 * FilterDropdown component
 *
 * @param {Object} props
 * @param {string} props.label - Dropdown label
 * @param {Array} props.options - Array of { value, label, sublabel? } — sublabel shows under the label in the open menu only
 * @param {string} props.value - Selected value
 * @param {function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text when no selection
 * @param {boolean} props.allowAll - Show "All" option (default: true)
 * @param {string} props.allLabel - Label for "All" option
 * @param {string} props.size - 'small' | 'medium'
 * @param {boolean} props.fullWidth - Full width (default: false)
 * @param {boolean} props.active - Highlight in the brand color to flag a narrowed selection
 */
function FilterDropdown({
  label,
  options = [],
  value,
  onChange,
  placeholder,
  allowAll = true,
  allLabel = "All",
  size = "small",
  fullWidth = false,
  active = false,
  minWidth = 140
}) {
  const handleChange = (event) => {
    const newValue = event.target.value;
    onChange(newValue === "__all__" ? null : newValue);
  };

  // When the client has narrowed this filter, tint it with the brand color so
  // the changed filters stand out from the untouched defaults at a glance.
  const activeSx = active
    ? {
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        color: "primary.main",
        fontWeight: 600,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main", borderWidth: 1.5 },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
        "& .MuiSelect-icon": { color: "primary.main" }
      }
    : undefined;

  return (
    <FormControl size={size} fullWidth={fullWidth} sx={{ minWidth }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={value || "__all__"}
        onChange={handleChange}
        label={label}
        displayEmpty={!label}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          PaperProps: { sx: { mt: 0.5 } }
        }}
        renderValue={(selected) => (selected === "__all__" ? placeholder || allLabel : (options.find((o) => o.value === selected)?.label ?? ""))}
        sx={activeSx}
      >
        {allowAll && <MenuItem value="__all__">{placeholder || allLabel}</MenuItem>}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={option.sublabel ? { flexDirection: "column", alignItems: "flex-start" } : undefined}>
            {option.label}
            {option.sublabel && (
              <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {option.sublabel}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default FilterDropdown;
